/**
 * Data-source seam for Leagues.
 *
 * The UI reads league state and dispatches actions through a single
 * `LeaguesDataSource` interface instead of importing a specific store. Two
 * adapters implement it:
 *   - `apiDataSource` (this file) — the real backend (leagueApiStore.ts + leagueApi.ts).
 *   - `prototypeDataSource` (leaguePrototypeDataSource.ts) — the client-side
 *     simulation, used for the always-on demo at /leaguesdemo.
 *
 * The app root provides `apiDataSource` globally; the demo route nests a
 * provider with `prototypeDataSource`. Keeping the prototype adapter in its own
 * module means the heavy demo seed data only loads with the demo chunk.
 *
 * `useLeaguesDataSource()` returns the active adapter.
 */

import React, { createContext, useContext } from "react";
import {
    createLeagueApi,
    declineLeagueApi,
    enterBullionBattleApi,
    findLeagueApi,
    findLeagueByCodeApi,
    getLeaguesApiSnapshot,
    inviteFriendsApi,
    joinLeagueApi,
    loadBullionBattleApi,
    loadLeagueByCode as apiLoadLeagueByCode,
    loadLeagueDetail as apiLoadLeagueDetail,
    placeLeaguePlayApi,
    refreshLeagues,
    subscribeToLeaguesApi,
    topUpLeagueSeatApi,
} from "./leagueApiStore";
import { LeagueApiError } from "./leagueApi";
import type {
    CreateLeagueInput,
    League,
    LeagueBullionBattle,
    LeagueBullionBattleViewerSlip,
    LeagueGameKey,
    LeaguesState,
} from "./types";

export type LeaguesDataSourceMode = "prototype" | "api";

/** The viewer identity call sites already have via `useLeagueViewer()`. */
export interface LeagueViewerIdentity {
    userId: number;
    name: string;
    avatar: string | null;
    xpWeekCrown: string | null;
}

/** Superset placement input covering both the demo and the real market-backed play. */
export interface DataSourcePlayInput {
    game: LeagueGameKey;
    stake: number;
    multiplier: number;
    selection?: string;
    /** Real market reference (api mode) — captured for settlement against cricket results. */
    matchId?: string;
    marketId?: string;
    marketLineId?: string;
    selectionKey?: string;
    settlesAt?: string;
    /** Trader/Squares market descriptor for settlement + Activity display. */
    marketDetails?: {
        marketclass?: string;
        selectionname?: string;
        marketline?: string | number;
        marketTypeId?: string | number;
        marketname?: string;
        inningsNumber?: number;
        overNumber?: number;
        totalChips?: number;
        selections?: Record<string, { run?: string; chips?: number; odds?: string | number }>;
    };
    /** Prototype-only: how long until the fake engine settles this play. */
    settleDelayMs?: number;
}

export interface LeaguesDataSource {
    mode: LeaguesDataSourceMode;
    subscribe: (listener: () => void) => () => void;
    getSnapshot: () => LeaguesState;
    /** Force a reload (api) / no-op (prototype). */
    refresh: () => void | Promise<void>;
    findLeague: (leagueId: string | undefined) => League | null;
    findLeagueByCode: (code: string | undefined) => League | null;
    /** Load a fresh copy of one league (api) / read from snapshot (prototype). */
    loadLeagueDetail: (leagueId: string, opts?: { fresh?: boolean }) => Promise<League | null>;
    loadLeagueByCode: (code: string) => Promise<League | null>;
    createLeague: (input: CreateLeagueInput, viewer: LeagueViewerIdentity) => Promise<League>;
    joinLeague: (
        target: { leagueId?: string; code?: string; inviterPlayerId?: string },
        viewer: LeagueViewerIdentity,
    ) => Promise<{ ok: boolean; league?: League; error?: string; errorCode?: string }>;
    /** Pay rubies for more league chips (+ grow prize pot). Cap is per member. */
    topUpSeat: (
        leagueId: string,
        viewer: LeagueViewerIdentity,
    ) => Promise<{ ok: boolean; league?: League; error?: string; errorCode?: string }>;
    declineInvite: (leagueId: string) => Promise<void>;
    inviteFriends: (leagueId: string, userIds: number[]) => Promise<void>;
    placePlay: (
        leagueId: string,
        input: DataSourcePlayInput,
        viewer: LeagueViewerIdentity,
    ) => Promise<{ ok: boolean; playId?: string; error?: string }>;
    /**
     * Ensure/refresh the league's shared Bullion battle. Api: server lazy-creates once the board is
     * ready; prototype: ensures a local battle. Returns the battle (or null with a `reason` — e.g.
     * `bullion_board_not_ready` — when the battle can't exist yet).
     */
    getBullionBattle: (
        leagueId: string,
    ) => Promise<{ battle: LeagueBullionBattle | null; reason: string | null }>;
    /** Enter (or replace) the viewer's Bullion battle slip; holds the league-coin entry stake. */
    enterBullionBattle: (
        leagueId: string,
        slip: LeagueBullionBattleViewerSlip,
        viewer: LeagueViewerIdentity,
    ) => Promise<{ ok: boolean; error?: string }>;
    /** Prototype-only VIP toggle; undefined in api mode (VIP is server-driven). */
    setVip?: (vip: boolean) => void;
}

function apiErrorMessage(e: unknown, fallback: string): string {
    return e instanceof LeagueApiError && e.message ? e.message : fallback;
}

function apiErrorCode(e: unknown): string | undefined {
    return e instanceof LeagueApiError ? e.code : undefined;
}

export const apiDataSource: LeaguesDataSource = {
    mode: "api",
    subscribe: subscribeToLeaguesApi,
    getSnapshot: getLeaguesApiSnapshot,
    refresh: () => refreshLeagues(),
    findLeague: (leagueId) => findLeagueApi(leagueId),
    findLeagueByCode: (code) => findLeagueByCodeApi(code),
    loadLeagueDetail: (leagueId, opts) => apiLoadLeagueDetail(leagueId, opts),
    loadLeagueByCode: (code) => apiLoadLeagueByCode(code),
    createLeague: async (input) => createLeagueApi(input),
    joinLeague: async (target) => {
        try {
            const league = await joinLeagueApi(target);
            return { ok: true, league };
        } catch (e) {
            return {
                ok: false,
                error: apiErrorMessage(e, "Could not join the league."),
                errorCode: apiErrorCode(e),
            };
        }
    },
    topUpSeat: async (leagueId) => {
        try {
            const league = await topUpLeagueSeatApi(leagueId);
            return { ok: true, league };
        } catch (e) {
            return {
                ok: false,
                error: apiErrorMessage(e, "Could not top up league balls."),
                errorCode: apiErrorCode(e),
            };
        }
    },
    declineInvite: (leagueId) => declineLeagueApi(leagueId),
    inviteFriends: async (leagueId, userIds) => {
        await inviteFriendsApi(leagueId, userIds);
    },
    placePlay: async (leagueId, input) => {
        try {
            const playId = await placeLeaguePlayApi(leagueId, {
                game: input.game,
                stake: input.stake,
                multiplier: input.multiplier,
                selection: input.selection,
                matchId: input.matchId,
                marketId: input.marketId,
                marketLineId: input.marketLineId,
                selectionKey: input.selectionKey,
                settlesAt: input.settlesAt,
                marketDetails: input.marketDetails,
            });
            return { ok: true, playId };
        } catch (e) {
            return { ok: false, error: apiErrorMessage(e, "Could not place the play.") };
        }
    },
    getBullionBattle: (leagueId) => loadBullionBattleApi(leagueId),
    enterBullionBattle: async (leagueId, slip) => {
        try {
            await enterBullionBattleApi(leagueId, slip);
            return { ok: true };
        } catch (e) {
            return { ok: false, error: apiErrorMessage(e, "Could not enter the Bullion battle.") };
        }
    },
};

const LeaguesDataSourceContext = createContext<LeaguesDataSource>(apiDataSource);

/**
 * Provide an explicit data source to a subtree. The app root provides
 * `apiDataSource`; the demo route nests this with `prototypeDataSource`.
 */
export const LeaguesDataSourceProvider: React.FC<{
    source: LeaguesDataSource;
    children: React.ReactNode;
}> = ({ source, children }) => {
    return <LeaguesDataSourceContext.Provider value={source}>{children}</LeaguesDataSourceContext.Provider>;
};

export function useLeaguesDataSource(): LeaguesDataSource {
    return useContext(LeaguesDataSourceContext);
}
